import { PayloadAction } from '@reduxjs/toolkit';
import { call, delay, fork, put, select } from 'redux-saga/effects';
import { SagaIterator } from '@redux-saga/types';
import { toast } from 'react-toastify';
import i18n from '@/i18n';

import { amlbotAPI } from '@/api';
import type {
  BaseRequest,
  BaseSuccessResponse,
  CreateReflinkRequest,
  DeleteBalanceAlertRequest,
  GetProfileInfoResponse,
  ReflinkResponse,
  SetBalanceAlertRequest,
  SetBalanceAlertResponse,
  UpdateReflinkRequest,
  UserPreferencesRequest,
  UserPreferencesResponse,
} from '@/api';

import {
  COMMON_CONTAINER_ID,
  ErrorCodeEnum,
  USER_MONITOR_DELAY,
} from '@/config';
import {
  criticalBalanceAlertModalActions,
  paymentActions,
  profileActions,
  userActions,
} from '@/redux/actions';
import { profileMappers } from '@/redux/mappers';
import { parseError } from '@/redux/helpers';
import { blockSelectors } from '@/redux/selectors';
import { setSentryUser } from '@/config/sentryInit';
import axios from 'axios';

function* getProfileInfo(): SagaIterator {
  try {
    const response: GetProfileInfoResponse = yield call(
      amlbotAPI.getProfileInfo,
    );
    const data = profileMappers.getProfileInfo(response);
    yield put(profileActions.setData(data));

    // Update Sentry user info with profile data
    const userData = yield select((state: any) => state.user.data);
    if (userData) {
      setSentryUser(userData, data);
    }
  } catch (error: unknown) {
    const { message } = parseError(error);
    yield put(profileActions.setError(message));
  } finally {
    yield put(profileActions.setLoading(false));
  }
}

function* monitorUser(): SagaIterator {
  try {
    yield delay(USER_MONITOR_DELAY);

    const isBlocked = yield select(blockSelectors.getIsBlocked);

    if (!isBlocked) {
      yield put(profileActions.getProfileInfo());
      yield put(userActions.current());
    }

    yield fork(monitorUser);
  } catch (error: unknown) {
    // unexpected
    console.log(error);
  }
}

function* updateAlertPeriod(
  action: PayloadAction<SetBalanceAlertRequest>,
): SagaIterator {
  try {
    const { payload } = action;
    const response: SetBalanceAlertResponse = yield call(
      amlbotAPI.setAlertPeriod,
      payload,
    );
    const data = profileMappers.getBalanceAlert(response);

    toast.success(i18n.t('translation.toast_balance_alert_set'), {
      containerId: COMMON_CONTAINER_ID,
    });

    yield put(criticalBalanceAlertModalActions.closeModal());
    yield put(profileActions.setBalanceAlertData(data));
  } catch (error: unknown) {
    const { message } = parseError(error);
    yield put(profileActions.setError(message));
  } finally {
    yield put(profileActions.setLoading(false));
  }
}

function* deleteBalanceAlert(
  action: PayloadAction<DeleteBalanceAlertRequest>,
): SagaIterator {
  try {
    yield call(amlbotAPI.deleteAlertPeriod);
    yield put(profileActions.setBalanceAlertData(null));
  } catch (error: unknown) {
    // console.log(error);
  } finally {
    yield put(profileActions.setLoading(false));
  }
}

function* fetchRefLinks(action: PayloadAction<BaseRequest>): SagaIterator {
  try {
    const payload: BaseRequest = {};
    const response: ReflinkResponse = yield call(
      amlbotAPI.fetchRefLinks,
      payload,
    );
    const data = profileMappers.getReflinksData(response);
    yield put(profileActions.setRefLinksData(data));
  } catch (error: unknown) {
    // console.log(error);
  } finally {
    yield put(profileActions.setLoading(false));
  }
}

function* createReflink(
  action: PayloadAction<CreateReflinkRequest>,
): SagaIterator {
  try {
    const { payload } = action;
    const response: ReflinkResponse = yield call(
      amlbotAPI.createReflink,
      payload,
    );
    const data = profileMappers.getReflinksData(response);
    yield put(profileActions.setRefLinksData(data));
  } catch (error: unknown) {
    // console.log(error);
  } finally {
    yield put(profileActions.setLoading(false));
  }
}

function* updateReflink(
  action: PayloadAction<UpdateReflinkRequest>,
): SagaIterator {
  try {
    const { payload } = action;
    const response: ReflinkResponse = yield call(
      amlbotAPI.updateReflink,
      payload,
    );
    const data = profileMappers.getReflinksData(response);
    yield put(profileActions.setRefLinksData(data));
  } catch (error: unknown) {
    // console.log(error);
  } finally {
    yield put(profileActions.setLoading(false));
  }
}

function* updatePreferences(
  action: PayloadAction<{
    payload: UserPreferencesRequest;
    callback?: () => void;
  }>,
): SagaIterator {
  try {
    yield put(profileActions.setLoadingPreferences(true));

    const { payload } = action.payload;
    const response: UserPreferencesResponse = yield call(
      amlbotAPI.updatePreferences,
      payload,
    );

    const isWebhookUpdate = payload.preferences.webhook?.url !== undefined;

    if (isWebhookUpdate) {
      toast.success(i18n.t('translation.webhook_url_success_update'), {
        containerId: COMMON_CONTAINER_ID,
      });
    } else {
      toast.success(i18n.t('translation.preferences_success_update'), {
        containerId: COMMON_CONTAINER_ID,
      });
    }

    const {
      data: { preferences, url },
    } = response;
    yield put(profileActions.setPreferences(preferences));
    yield put(profileActions.setWebhookUrl(url));

    if (action.payload.callback) {
      action.payload.callback();
      yield put(profileActions.setError(null));
    }
  } catch (error: unknown) {
    const { message, errorCode } = parseError(error);
    if (errorCode === ErrorCodeEnum.WEBHOOK_TEST_FAILED) {
      yield put(
        profileActions.setError(i18n.t('translation.webhook_set_error')),
      );
    } else {
      toast.error(message, { containerId: COMMON_CONTAINER_ID });
    }
  } finally {
    yield put(profileActions.setLoadingPreferences(false));
  }
}

function* pickUsage(action: PayloadAction<BaseRequest>): SagaIterator {
  try {
    const payload: BaseRequest = {};
    const response: BaseSuccessResponse = yield call(
      amlbotAPI.usagePicked,
      payload,
    );
  } catch (error: unknown) {
    // console.log(error);
  } finally {
    yield put(profileActions.setLoadingPreferences(false));
  }
}

export {
  getProfileInfo,
  updateAlertPeriod,
  deleteBalanceAlert,
  fetchRefLinks,
  createReflink,
  updateReflink,
  monitorUser,
  updatePreferences,
  pickUsage,
};
